import React, { useEffect, useCallback, useMemo } from "react";
import cns from "classnames";
import { Observer } from "mobx-react";
import { AutoSizer, defaultTableRowRenderer, TableRowRenderer } from "react-virtualized";
import { Paper, withStyles } from "@material-ui/core";

import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchResultLayout from "@search/components/SearchResultLayout";
import SearchOptions from "@search/SearchOptions";
import TableSearch from "@search/TableSearch/TableSearch";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import TableSubheader from "@search/TableSearch/TableSubheader";
import TableSearchHeader from "@search/TableSearch/TableSearchHeader";

import { BookingScheduleSearchStore } from "./BookingScheduleSearchStore";
import { getColumns } from "./BookingScheduleSearchColumns";
import { BookingSearchForm } from "../BookingSearchForm";
import { BookingFragment, RowType } from "./utils";
import { BookingFilterGroup, groupBy } from "../utils";
import { getPollLimit } from "../poll-limit";
import { BookingSearchActionBar } from "../BookingSearchActionBar";
import { Classes, ThemeType } from "@theme";
import { usePolling } from "@bonliva-crm/ui";

type Props = {
  classes: Classes<typeof styles>;
  onEdit: (id?: string) => void;
  onGroupBy: (groupBy: BookingFilterGroup) => void;
  onOpen: (id: string, tab: string, scrollContainer?: HTMLElement) => void;
};

const BookingScheduleSearch: React.FC<Props> = ({
  classes,
  onEdit,
  onGroupBy,
  onOpen
}) => {
  const store = useMemo(() => new BookingScheduleSearchStore(), []);

  useEffect(() => {
    return () => {
      store.unsubscribe();
    };
  }, [store]);

  usePolling(
    async () => {
      await store.queryFuncWatch.refetch({
        filter: store.filterManager.queryInput,
        limit: getPollLimit(store.searchManager.items?.length),
        skip: 0
      });
    },
    "/bookings",
    { interval: 5000, refetchOnPushState: true }
  );

  const columns = useMemo(() => getColumns(store), [store]);

  const renderFilter = useCallback(() => {
    return (
      <div style={{ position: "relative" }}>
        <BookingSearchForm
          manager={store.filterManager}
          group={BookingFilterGroup.Schedule}
        />
      </div>
    );
  }, [store.filterManager]);

  const renderGroupHeadline = useCallback(() => {
    const { searchManager } = store;

    return (
      <Observer>
        {() => {
          if (!searchManager.header) {
            return null;
          }

          return (
            <TableSubheader
              center
              mainText={searchManager.header.week}
              smallText={searchManager.header.year}
            />
          );
        }}
      </Observer>
    );
  }, [store.searchManager]);

  const rowRenderer: TableRowRenderer = useCallback(
    (rowProps) => {
      const row: RowType = rowProps.rowData;

      if (row.type === "group") {
        return (
          <div
            className={cns(classes.subHeader, rowProps.className)}
            aria-rowindex={rowProps.index}
            key={row._id}
            role="row"
            style={{
              ...rowProps.style,
              width: rowProps.style.width - 1
            }}
          >
            <TableSubheader
              center
              mainText={row.header.week}
              smallText={row.header.year}
            />
          </div>
        );
      }

      return defaultTableRowRenderer({
        ...rowProps,
        rowData: row.data
      });
    },
    [classes.subHeader]
  );

  return (
    <SearchPageLayout
      delayed
      wrapContent={(content) => <Paper>{content}</Paper>}
      actionBar={
        <>
          <Observer>
            {() => (
              <BookingSearchActionBar
                onDirectBooking={() => onEdit(null)}
                total={store.total}
                withTotal
              />
            )}
          </Observer>

          <SearchOptions
            activeGroupBy={BookingFilterGroup.Schedule}
            groupBy={groupBy()}
            onGroupBy={(v) => onGroupBy(v.value)}
          />
        </>
      }
      content={(scrollContainer) => (
        <SearchResultLayout fittedContent fitted style={{ maxWidth: 9999 }}>
          <AutoSizer disableHeight className={classes.stickyHeader}>
            {({ width }) => (
              <div>
                <ColumnSizer columns={columns} width={width}>
                  {({ ref }) => (
                    <TableSearchHeader
                      columns={columns}
                      columnSizer={ref}
                      style={{ backgroundColor: "#fff" }}
                    />
                  )}
                </ColumnSizer>
                <div style={{ width }}>{renderGroupHeadline()}</div>
              </div>
            )}
          </AutoSizer>

          <div>
            <TableSearch
              disableHeader
              dynamicColIndex={1}
              scrollContainer={scrollContainer}
              manager={store.searchManager}
              columns={columns}
              rowRenderer={rowRenderer}
              rowStyle={{ cursor: "pointer" }}
              onRowClick={(info) => {
                const row = info.rowData as unknown as BookingFragment;
                onOpen(row.BookingRef.NodeID, "schedule", scrollContainer);
              }}
              rowHeight={({ index }) => {
                const row = store.searchManager.getRow(index);
                return row.type === "group" ? 48 : 80;
              }}
              onRowsRendered={store.searchManager.onRowsRendered}
            />
          </div>
        </SearchResultLayout>
      )}
      filter={renderFilter()}
    />
  );
};

const styles = (theme: ThemeType) => ({
  subHeader: {
    display: "flex",
    height: 48,
    pointerEvents: "none"
  },

  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 1
  }
});

  export default withStyles(styles as any)(BookingScheduleSearch);
